import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import Typography from 'ravenjs/lib/Typography';
import { callFunc, get, getHandler, isEmpty } from 'ravenjs/utils';
import { COLOR_TOKENS } from 'constants/quartz/color_tokens';

const PremiumSolutionsContainer = styled.div`
    margin: ${({ margin }) => margin};
`;
const PremiumSolutionsTooltip = styled.span`
    background-color: ${COLOR_TOKENS.background.inverse};
    border-radius: 4px;
    bottom: calc(100% + 8px);
    color: ${COLOR_TOKENS.text.inverse};
    display: none;
    font-size: 12px;
    left: 0;
    line-height: 16px;
    padding: 8px 12px;
    pointer-events: none;
    position: absolute;
    white-space: nowrap;
    z-index: 10;
`;
const PremiumSolutionsLinkWrapper = styled.span`
    display: inline-block;
    position: relative;

    &:hover ${PremiumSolutionsTooltip}, &:focus-within ${PremiumSolutionsTooltip} {
        display: block;
    }
`;
const PremiumSolutionsNote = styled(Typography)`
    font-size: 12px;
    line-height: 16px;
`;
const PremiumSolutionsLink = styled.button`
    background: none;
    border: none;
    box-shadow: none;
    color: ${({ disabled }) => (disabled ? COLOR_TOKENS.text.disabled : COLOR_TOKENS.text.link)};
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    font-size: 14px;
    line-height: 20px;
    outline: none;
    padding: 0;
    text-decoration: underline;

    &:focus,
    &:active {
        box-shadow: none;
        outline: none;
    }
`;

/**
 * "Add/Manage Premium Solutions" link with the billing note, rendered under a configuration.
 *
 * @method      PremiumSolutions
 * @param       {Object}           props Component props
 * @constructor
 */
function PremiumSolutions({ disabled, formContext, margin, premiumSolutions }) {
    if (isEmpty(premiumSolutions)) {
        return null;
    }
    const {
        isDisabled,
        disabledText,
        linkText,
        noteLabel,
        noteText,
        onClickRef,
    } = premiumSolutions;
    const isLinkDisabled = Boolean(isDisabled || disabled);
    const handleOnClick = () => {
        if (!isLinkDisabled) {
            callFunc(getHandler(get(formContext, 'handlers', {}), onClickRef));
        }
    };

    return (
        <PremiumSolutionsContainer margin={margin}>
            <PremiumSolutionsLinkWrapper>
                {isLinkDisabled && disabledText && (
                    <PremiumSolutionsTooltip role="tooltip">{disabledText}</PremiumSolutionsTooltip>
                )}
                <PremiumSolutionsLink
                    aria-disabled={isLinkDisabled}
                    disabled={isLinkDisabled}
                    onClick={handleOnClick}
                    type="button"
                >
                    {linkText}
                </PremiumSolutionsLink>
            </PremiumSolutionsLinkWrapper>
            <PremiumSolutionsNote gutterBottom="0" gutterTop="2px">
                <strong>{noteLabel}</strong> {noteText}
            </PremiumSolutionsNote>
        </PremiumSolutionsContainer>
    );
}

PremiumSolutions.propTypes = {
    disabled: PropTypes.bool,
    formContext: PropTypes.object,
    margin: PropTypes.string,
    premiumSolutions: PropTypes.object,
};

PremiumSolutions.defaultProps = {
    disabled: false,
    formContext: {},
    margin: '0',
    premiumSolutions: null,
};

export default PremiumSolutions;
